import * as i1 from '@angular/cdk/a11y';
import { A11yModule } from '@angular/cdk/a11y';
import * as i0 from '@angular/core';
import { NgModule, Injectable } from '@angular/core';
import * as i2 from '@yourcause/common/heap';
import { TrackingEventNames, TrackingPropertyNames } from '@yourcause/common/heap';
import * as toastr from 'toastr';

class NotifierModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: NotifierModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.2.10", ngImport: i0, type: NotifierModule, imports: [A11yModule] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: NotifierModule, imports: [A11yModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: NotifierModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        A11yModule
                    ]
                }]
        }] });

let callbackTracker = 0;
class NotifierService {
    constructor(liveAnnouncer, heapService) {
        this.liveAnnouncer = liveAnnouncer;
        this.heapService = heapService;
        this.announcementCountMap = {};
        window.toastr = this;
        const toastOptions = {
            preventDuplicates: true
        };
        this.toastrOptions = Object.assign(toastr.options, toastOptions);
    }
    getHeap() {
        if (window.heap) {
            return window.heap;
        }
        else {
            return null;
        }
    }
    announce(text) {
        let count = this.announcementCountMap[text] || 0;
        ++count;
        if (count === 1) {
            this.liveAnnouncer.announce(text);
        }
        this.announcementCountMap[text] = count;
    }
    replaceAnnouncement(text, nextText) {
        let count = this.announcementCountMap[text] || 0;
        --count;
        if (count === 0) {
            this.liveAnnouncer.announce(nextText);
        }
        this.announcementCountMap[text] = count;
    }
    assignOptions(options = {}) {
        Object.assign(toastr.options, options);
        return toastr.options;
    }
    // https://ux.stackexchange.com/questions/11203/how-long-should-a-temporary-notification-toast-appear
    setOptions(message, type, title, actionCallback, actionLabel = 'Undo') {
        let callbackIndexString;
        if (actionCallback) {
            callbackTracker++;
            callbackIndexString = '_toastrActionCallbacks' + callbackTracker;
            window[callbackIndexString] = function () {
                actionCallback();
                delete window[callbackIndexString];
            };
            const buttonString = '<br/><button class="btn btn-secondary btn-round yc-button toast-action" onclick="window.'
                + callbackIndexString + '()">' + actionLabel + '</button>';
            message += buttonString;
        }
        const icon = this.getIconForType(type);
        const iconString = `<i class="fa fa-${icon}"
      [style.color]="yc-default.alert-text-${type}">
      </i>`;
        message = iconString + `<div class="alert-text">` + message + `</div>`;
        let timeOut = 0;
        // JS - Hijacking Options and passing in 'sticky' to set both timeOut and extendedTimeOut to 0.
        //    - if you have a better idea on how to stash this setting, feel free.
        if (!this.toastrOptions.sticky) {
            const numChar = (message || '').length + (title || '').length;
            timeOut = numChar * 50;
            if (timeOut < 3000) {
                timeOut = 3000;
            }
            else if (timeOut > 7000) {
                timeOut = 7000;
            }
        }
        if (timeOut === 0) {
            this.assignOptions({ closeButton: true, progressBar: false });
        }
        else {
            this.assignOptions({ closeButton: false, progressBar: true });
        }
        this.assignOptions({ timeOut, extendedTimeOut: timeOut * 2 });
        return message;
    }
    success(message, title, actionCallback, actionLabel = 'Undo') {
        message = this.setOptions(message, 'success', title, actionCallback, actionLabel);
        toastr.success(message, title);
    }
    error(message, title, actionCallback, actionLabel = 'Undo') {
        message = this.setOptions(message, 'error', title, actionCallback, actionLabel);
        // track event
        this.heapService.track(TrackingEventNames.Error, {
            [TrackingPropertyNames.Message]: message
        });
        toastr.error(message);
    }
    errorSticky(message) {
        this.assignOptions({ sticky: true });
        this.error(message);
        this.assignOptions({ sticky: false });
    }
    warning(message, title, actionCallback, actionLabel = 'Undo') {
        message = this.setOptions(message, 'warning', title, actionCallback, actionLabel);
        toastr.warning(message, title);
    }
    info(message, title, actionCallback, actionLabel = 'Undo') {
        message = this.setOptions(message, 'info', title, actionCallback, actionLabel);
        toastr.info(message);
    }
    clear() {
        toastr.clear();
    }
    getIconForType(type) {
        switch (type) {
            case 'success':
                return 'check';
            case 'error':
                return 'exclamation-circle';
            case 'warning':
                return 'exclamation-triangle';
            case 'info':
                return 'info';
        }
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: NotifierService, deps: [{ token: i1.LiveAnnouncer }, { token: i2.HeapService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: NotifierService, providedIn: NotifierModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: NotifierService, decorators: [{
            type: Injectable,
            args: [{ providedIn: NotifierModule }]
        }], ctorParameters: () => [{ type: i1.LiveAnnouncer }, { type: i2.HeapService }] });

/**
 * Generated bundle index. Do not edit.
 */

export { NotifierModule, NotifierService };
